// Globals
import { Key, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TreeProps } from 'antd';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getCategories, selectCategories, selectCategoriesIsLoading } from '@/modules/Categories';

// Models
import { ICategory } from '@/models/categories.model';

// Components
import { Input, Skeleton } from '@/ui';
import { ChevronIcon } from '@/icons';

// Styles
import { StyledPanel, StyledSearchWrap, StyledTree, StyledEmpty } from './SeoCategoryTreeStyles';

export interface ISeoCategoryTreeProps {
  selectedId: string | null;
  onSelect: (category: { id: string; name: string }) => void;
}

interface ITreeNode {
  title: string;
  key: string;
  children?: ITreeNode[];
}

const buildTreeData = (categories: ICategory[]): ITreeNode[] =>
  categories.map((category) => ({
    title: category.name ?? '',
    key: category.id,
    ...(category.sub_categories?.length ? { children: buildTreeData(category.sub_categories) } : {}),
  }));

// Keeps a node when it (or any descendant) matches the search; ancestors of a
// match are preserved so the matched leaf stays reachable in the tree.
const filterTree = (nodes: ITreeNode[], search: string): ITreeNode[] =>
  nodes.reduce<ITreeNode[]>((acc, node) => {
    const children = node.children ? filterTree(node.children, search) : undefined;
    const isMatch = (node.title ?? '').toLowerCase().includes(search);

    if (isMatch || children?.length) {
      acc.push({ ...node, ...(children?.length ? { children } : {}) });
    }

    return acc;
  }, []);

const collectKeys = (nodes: ITreeNode[]): Key[] =>
  nodes.flatMap((node) => [node.key, ...(node.children ? collectKeys(node.children) : [])]);

const SeoCategoryTree = ({ selectedId, onSelect }: ISeoCategoryTreeProps) => {
  const { t } = useTranslation(['seo']);
  const dispatch = useAppDispatch();
  const categories = useAppState(selectCategories);
  const isLoading = useAppState(selectCategoriesIsLoading);
  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [autoExpand, setAutoExpand] = useState(true);

  useEffect(() => {
    if (!categories?.length) {
      dispatch(getCategories());
    }
  }, []);

  const treeData = useMemo(() => buildTreeData(categories ?? []), [categories]);

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? filterTree(treeData, term) : treeData;
  }, [treeData, search]);

  const handleSearch = (value: string) => {
    setSearch(value);
    // While searching, expand every branch so matched descendants stay visible.
    setExpandedKeys(value.trim() ? collectKeys(filterTree(treeData, value.trim().toLowerCase())) : []);
    setAutoExpand(true);
  };

  const handleExpand: TreeProps['onExpand'] = (keys) => {
    setExpandedKeys(keys);
    setAutoExpand(false);
  };

  const handleSelect: TreeProps['onSelect'] = (_keys, info) => {
    onSelect({ id: String(info.node.key), name: String(info.node.title ?? '') });
  };

  return (
    <StyledPanel>
      <StyledSearchWrap>
        <Input
          size="large"
          allowClear
          placeholder={t('seo_filters_search_placeholder')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </StyledSearchWrap>

      {isLoading && !categories?.length ? (
        <Skeleton active paragraph={{ rows: 6 }} title={false} />
      ) : filteredData.length ? (
        <StyledTree
          treeData={filteredData}
          blockNode
          showIcon={false}
          switcherIcon={<ChevronIcon />}
          selectedKeys={selectedId ? [selectedId] : []}
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpand}
          onExpand={handleExpand}
          onSelect={handleSelect}
        />
      ) : (
        <StyledEmpty>{t('seo_filters_categories_empty')}</StyledEmpty>
      )}
    </StyledPanel>
  );
};

export default SeoCategoryTree;
